import {
	Button,
	Inline,
	Stack,
	Box,
	Divider,
	Heading,
	Text,
	Icons,
	Grid,
	Col,
	Select,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	DurationInputField,
	FieldArray,
	Form,
	GetFieldValue,
	MarkdownInputField,
	SelectField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
	EmptyNumberValidator,
	isTruthy,
	SwitchInputField,
	DateRangePickerField,
	CheckboxInputField,
	FormSpy,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { useRef, useState } from "react"
import { Optional, Required } from "utility-types"
import * as Validator from "yup"
import { useAuthUser } from "../Auth"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { TTravelActivity } from "./store"
import { parseTouristConfigurationsString } from "./utils"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	LocationFields,
	TTransportServiceLocationPoint,
	HotelAsPickupDropToggleField,
} from "../TransportServices"

export function AddActivityInDialog({
	onSuccess,
	onClose,
	open,
	...props
}: {
	open: boolean
	onClose: () => void
} & Omit<React.ComponentProps<typeof AddNewTravelActivityItem>, "onCancel">) {
	return (
		<Dialog open={open} onClose={onClose} title="Add Activity" lg>
			<Dialog.Body>
				<AddNewTravelActivityItem
					onSuccess={(activity) => {
						onClose()
						onSuccess?.(activity)
						showSnackbar(`Activity/Ticket ${activity.name} successfully added.`)
					}}
					onCancel={() => onClose()}
					{...props}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

type TNewItemValue = {
	name: string
	description?: string
	complimentary_till_age?: number
	required_minimum_age?: number
	allowed_maximum_age?: number
	same_closing_days_and_intervals_for_all_tickets: 0 | 1 | boolean | "0" | "1"
	closed_on_days_of_week?: Array<string>
	closed_on_date_intervals?: Array<{ start_date?: Date; end_date?: Date }>
	use_same_locations: boolean
	pick_up_locations?: Array<TTransportServiceLocationPoint | string | undefined>
	drop_locations?: Array<TTransportServiceLocationPoint | string | undefined>
	set_hotel_as_default_pickup?: string | null
	set_hotel_as_default_drop?: string | null
	ticket_types?: Array<{
		opens_at?: string
		closes_at?: string
		name: string
		short_name?: string
		description?: string
		closed_on_days_of_week?: Array<string>
		closed_on_date_intervals?: Array<{ start_date?: Date; end_date?: Date }>
		durations?: Array<{
			iso?: string
			slots?: string
		}>
		pick_up_locations?: Array<
			TTransportServiceLocationPoint | string | undefined
		>
		drop_locations?: Array<TTransportServiceLocationPoint | string | undefined>
		set_hotel_as_default_pickup?: string | null
		set_hotel_as_default_drop?: string | null
	}>
	ticket_tourist_configurations: string
	trip_destinations?: Array<TTripDestination>
	quick_add?: boolean
}

const INITIAL_VALUES: Required<TNewItemValue, "ticket_types"> = {
	name: "",
	description: "",
	complimentary_till_age: undefined,
	required_minimum_age: undefined,
	allowed_maximum_age: undefined,
	same_closing_days_and_intervals_for_all_tickets: 1,
	use_same_locations: true,
	pick_up_locations: [undefined],
	drop_locations: [undefined],
	set_hotel_as_default_pickup: undefined,
	set_hotel_as_default_drop: undefined,
	ticket_types: [
		{
			name: "",
			short_name: "",
			description: "",
			durations: [
				{
					iso: "",
					slots: "",
				},
			],
			pick_up_locations: [undefined],
			drop_locations: [undefined],
			set_hotel_as_default_pickup: undefined,
			set_hotel_as_default_drop: undefined,
		},
	],
	ticket_tourist_configurations: "Adult, Child (6-12)",
}
export function AddNewTravelActivityItem({
	onSuccess,
	...props
}: {
	onSuccess: (activity: TTravelActivity) => void
} & Omit<
	React.ComponentProps<typeof AddNewTravelActivityItemForm>,
	"onSubmit"
>) {
	const xhr = useXHR()
	return (
		<AddNewTravelActivityItemForm
			{...props}
			onSubmit={async (data) => {
				const resp = await xhr.post<{ data: TTravelActivity }>(
					"/travel-activities",
					data
				)
				onSuccess(resp.data.data)
			}}
		/>
	)
}

export function AddNewTravelActivityItemForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues = {},
}: {
	onSubmit: (data: {
		name: string
		description?: string
		complimentary_till_age?: number
		ticket_types: Array<{
			name: string
			short_name?: string
			description?: string
			closed_on_days_of_week?: Array<string>
			closed_on_date_intervals?: Array<[string, string]>
			durations?: Array<{
				iso: string | null
				slots?: Array<string>
			}>
			pick_up_locations?: string[]
			drop_locations?: string[]
			set_hotel_as_default_pickup?: string | null
			set_hotel_as_default_drop?: string | null
		}>
		ticket_tourist_configurations: Array<string>
		trip_destinations: Array<number>
	}) => Promise<void>
	onCancel: () => void
	initialValues?: Optional<TNewItemValue>
}) {
	const { user } = useAuthUser()
	const { name, trip_destinations, ...restData } = propInitialValues
	const initialQuickAddValue = !Object.values(restData).length
	const can_attach_services_to_destinations = Boolean(
		user?.tenant?.feature_flags?.attach_services_to_destinations
	)

	// Track if checkbox should be disabled (initially checked then unchecked)
	const [isCheckboxDisabled, setIsCheckboxDisabled] =
		useState<boolean>(!initialQuickAddValue)
	const validationSchema = Validator.object().shape({
		name: Validator.string()
			.required("Please provide a name for the activity")
			.max(191, "Please use 191 or lesser characters for the name."),
		complimentary_till_age: EmptyNumberValidator()
			.integer("Please use an integer value")
			.min(0, "Please use a positive integer value e.g. 3"),
		required_minimum_age: EmptyNumberValidator()
			.integer("Please use an integer value")
			.min(0, "Please use a positive integer value e.g. 3"),
		allowed_maximum_age: EmptyNumberValidator()
			.integer("Please use an integer value")
			.min(1, "Please use a positive integer value e.g. 3"),
		description: Validator.string()
			.nullable()
			.max(10000, "Please use 10000 or less characters for the details."),
		ticket_types: Validator.array().of(
			Validator.object().shape({
				name: Validator.string()
					.required("Please provide a name for the type")
					.max(191, "Please use 191 or lesser characters for the name."),
				short_name: Validator.string()
					.nullable()
					.max(191, "Please use 191 or lesser characters for the short_name."),
				description: Validator.string()
					.nullable()
					.max(10000, "Please use 10000 or less characters for the details."),
			})
		),
		ticket_tourist_configurations: Validator.string().required(
			"Please provide tourist age and group configuration for this activity."
		),
		trip_destinations: can_attach_services_to_destinations
			? Validator.array()
					.required("Please select a Destinations")
					.min(1, "Please select atleast one Destinations")
			: Validator.array().nullable(),
		quick_add: Validator.boolean(),
	})
	const initialValues = useRef<TNewItemValue>({
		...INITIAL_VALUES,
		...propInitialValues,
		trip_destinations: !can_attach_services_to_destinations
			? undefined
			: propInitialValues.trip_destinations || INITIAL_VALUES.trip_destinations,
		quick_add: initialQuickAddValue,
	})
	return (
		<Form<TNewItemValue>
			initialValues={initialValues.current}
			validate={validateFormValues(validationSchema)}
			onSubmit={withServerErrors(async ({ quick_add, ...values }) => {
				// When switch is checked, use shared top-level locations for all services
				const sharedPickUpLocations = values.use_same_locations
					? (values.pick_up_locations
							?.map((l) => (typeof l === "object" ? l?.name : l))
							.filter(Boolean) as string[])
					: undefined
				const sharedDropLocations = values.use_same_locations
					? (values.drop_locations
							?.map((l) => (typeof l === "object" ? l?.name : l))
							.filter(Boolean) as string[])
					: undefined
				const sharedSetHotelAsDefaultPickup = values.use_same_locations
					? values.set_hotel_as_default_pickup
					: undefined
				const sharedSetHotelAsDefaultDrop = values.use_same_locations
					? values.set_hotel_as_default_drop
					: undefined
				const data = {
					name: values.name,
					description: values.description,
					complimentary_till_age: values.complimentary_till_age,
					ticket_types:
						values.ticket_types?.map((s) => {
							const effectiveSetHotelAsPickup =
								sharedSetHotelAsDefaultPickup ?? s.set_hotel_as_default_pickup
							const effectiveSetHotelAsDrop =
								sharedSetHotelAsDefaultDrop ?? s.set_hotel_as_default_drop

							const effectivePickUpLocations =
								sharedPickUpLocations ??
								(s.pick_up_locations
									?.map((l) => (typeof l === "object" ? l.name : l))
									.filter(Boolean) as string[])

							const effectiveDropLocations =
								sharedDropLocations ??
								(s.drop_locations
									?.map((l) => (typeof l === "object" ? l.name : l))
									.filter(Boolean) as string[])
							return {
								opens_at: s.opens_at,
								closes_at: s.closes_at,
								name: s.name,
								short_name: s.short_name,
								description: s.description,
								closed_on_days_of_week: isTruthy(
									values.same_closing_days_and_intervals_for_all_tickets
								)
									? values.closed_on_days_of_week
									: s.closed_on_days_of_week,
								closed_on_date_intervals: (
									(isTruthy(
										values.same_closing_days_and_intervals_for_all_tickets
									)
										? values.closed_on_date_intervals
										: s.closed_on_date_intervals) || []
								)
									.filter((d): d is Required<typeof d> =>
										Boolean(d.start_date || d.end_date)
									)
									.map((d) => {
										const start_date = d.start_date
											? formatDate(d.start_date, "YYYY-MM-DD")
											: undefined
										const end_date = d.end_date
											? formatDate(d.end_date, "YYYY-MM-DD")
											: undefined

										if (start_date || end_date) {
											return [
												start_date || end_date,
												end_date || start_date,
											] as [string, string]
										}
										return undefined
									})
									.filter(
										(d): d is Exclude<typeof d, undefined> => d !== undefined
									),
								durations: s.durations
									? s.durations
											.map((d) => {
												return {
													iso: d.iso || null,
													slots: (d.slots || "")
														.split(",")
														.map((s) => s.trim())
														.filter(Boolean)
														.map((s) => {
															try {
																const date = parseDate(s, "HH:mm")
																return formatDate(date, "HH:mm")
															} catch (e) {
																return s
															}
														}),
												}
											})
											.filter((d): d is { iso: string; slots: Array<string> } =>
												Boolean(d)
											)
									: undefined,
								pick_up_locations: effectivePickUpLocations,
								drop_locations: effectiveDropLocations,
								set_hotel_as_default_pickup: effectiveSetHotelAsPickup,
								set_hotel_as_default_drop: effectiveSetHotelAsDrop,
							}
						}) || [],
					ticket_tourist_configurations: values.ticket_tourist_configurations
						.split(",")
						.map((t) => t.trim())
						.filter(Boolean),
					trip_destinations:
						values.trip_destinations?.length &&
						can_attach_services_to_destinations
							? values.trip_destinations.map((t) => t.id)
							: [],
				}
				await onSubmit(data)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					{initialQuickAddValue && (
						<Inline paddingBottom="2" justifyContent="end">
							<Box
								paddingX="2"
								paddingY="1"
								borderRadius="md"
								borderWidth="1"
								borderColor="primary"
							>
								<CheckboxInputField
									label="Quick Add"
									name="quick_add"
									disabled={isCheckboxDisabled}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										form.change("quick_add", e.target.checked)
										if (!e.target.checked) {
											setIsCheckboxDisabled(true)
										}
									}}
								/>
							</Box>
						</Inline>
					)}
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Heading fontSize="md">Activity Details</Heading>
								<Text color="muted">
									Please provide basic details regarding the activity.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<TextInputField
									name="name"
									label="Name"
									required
									placeholder="e.g. Port Blair To Havelock : Private Catamaran Ferry"
									maxWidth="md"
								/>
								<GetFieldValue<boolean> name="quick_add">
									{({ value: quickAdd }) => (
										<>
											{quickAdd ? null : (
												<>
													<MarkdownInputField
														name="description"
														label="Itinerary/Details"
														secondaryLabel="optional"
														placeholder="Some details regarding the activity"
													/>
													{initialQuickAddValue ? (
														<>
															<SwitchInputField
																name="use_same_locations"
																label="Use same pick-up/drop point for All Services"
															/>
															<GetFieldValue<boolean> name="use_same_locations">
																{({ value: useSameLocations }) =>
																	useSameLocations ? (
																		<Inline gap="4" flexWrap="wrap">
																			<Stack gap="2">
																				<Inline gap="1" alignItems="center">
																					<Text
																						as="label"
																						fontWeight="semibold"
																					>
																						Pick Up Locations
																					</Text>
																					<Text fontSize="sm" color="muted">
																						(optional)
																					</Text>
																				</Inline>
																				<LocationFields name="pick_up_locations" />
																				<HotelAsPickupDropToggleField
																					name="set_hotel_as_default_pickup"
																					for="pickup"
																				/>
																			</Stack>
																			<Stack gap="2">
																				<Inline gap="1" alignItems="center">
																					<Text
																						as="label"
																						fontWeight="semibold"
																					>
																						Drop Locations
																					</Text>
																					<Text fontSize="sm" color="muted">
																						(optional)
																					</Text>
																				</Inline>
																				<LocationFields name="drop_locations" />
																				<HotelAsPickupDropToggleField
																					name="set_hotel_as_default_drop"
																					for="drop"
																				/>
																			</Stack>
																		</Inline>
																	) : null
																}
															</GetFieldValue>
														</>
													) : null}
												</>
											)}
										</>
									)}
								</GetFieldValue>
								{can_attach_services_to_destinations ? (
									<SelectField
										select={SelectTripDestination}
										name="trip_destinations"
										label="Trip Destinations"
										multiple
										closeOnSelect
									/>
								) : null}
							</Stack>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Heading fontSize="md">
									Tourist Age and Group Configurations and Closing Day/Dates
								</Heading>
								<Text color="muted">
									Please provide tourist age and group configuration for tickets
									available in this activity e.g. Adult, Child(3-5),
									Child(6-12), Group (8 Pax) (2y+).
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<TextInputField
									label="Tourist Age Configuration"
									name="ticket_tourist_configurations"
									placeholder="e.g. Adult, Child(3-5), Child(6-12), Group(8 Pax)"
									type="text"
									maxWidth="md"
									help={
										<GetFieldValue<string> name="ticket_tourist_configurations">
											{({ value }) => (
												<Text>
													{parseTouristConfigurationsString(value)
														.map((v) => v.name)
														.join(", ")}
												</Text>
											)}
										</GetFieldValue>
									}
								/>
								<GetFieldValue<boolean> name="quick_add">
									{({ value: quickAdd }) => (
										<>
											{quickAdd ? null : (
												<>
													<TextInputField
														name="complimentary_till_age"
														label="Complimentary Age"
														type="number"
														min={0}
														placeholder="e.g. 4"
														style={{ width: "100px" }}
														help={
															<GetFieldValue<
																number | string
															> name="complimentary_till_age">
																{({ value }) => {
																	const age = parseInt(String(value))
																	if (isNaN(age)) return null
																	if (!age) return <Text>No one</Text>
																	return <Text>{age} yrs and below</Text>
																}}
															</GetFieldValue>
														}
													/>
													<SwitchInputField
														name="same_closing_days_and_intervals_for_all_tickets"
														label="Use same closing days/dates for All Ticket Types"
													/>
													<GetFieldValue<
														"" | boolean
													> name="same_closing_days_and_intervals_for_all_tickets">
														{({ value }) =>
															!isTruthy(value) ? null : (
																<Stack
																	gap="4"
																	paddingLeft="4"
																	paddingBottom="4"
																	borderLeftWidth="1"
																>
																	<SelectClosedOnDaysOfWeekField
																		name={`closed_on_days_of_week`}
																	/>
																	<SelectClosedOnDateIntervalsField
																		name={`closed_on_date_intervals`}
																	/>
																</Stack>
															)
														}
													</GetFieldValue>
												</>
											)}
										</>
									)}
								</GetFieldValue>
							</Stack>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Heading fontSize="md">Ticket Type/Packages</Heading>
								<Text color="muted">
									Please provide the ticket types or packages available for this
									activity.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Box paddingY="2">
								<FieldArray<
									Required<
										TNewItemValue,
										"ticket_types"
									>["ticket_types"][number]
								> name="ticket_types">
									{({ fields }) => (
										<Stack gap="2">
											{fields.map((name, index) => (
												<Stack
													key={name}
													paddingLeft="4"
													borderBottomWidth="1"
													borderLeftColor="emphasis"
													paddingBottom="2"
													borderLeftWidth="2"
													gap="4"
												>
													<Inline gap="6" flexWrap="wrap" collapseBelow="sm">
														<Inline
															flex="1"
															justifyContent="between"
															flexWrap="wrap"
															gap="1"
														>
															<Inline gap="1" flex="1">
																<Box flex="1">
																	<Stack gap="4">
																		<TextInputField
																			name={`${name}.name`}
																			label="Name"
																			placeholder="e.g.  Premium"
																			type="text"
																		/>
																		<GetFieldValue<boolean> name="quick_add">
																			{({ value: quickAdd }) => (
																				<>
																					{quickAdd ? null : (
																						<TextInputField
																							name={`${name}.short_name`}
																							label="Internal Ref Code"
																							secondaryLabel="optional"
																							placeholder="e.g. 1PXABC"
																							maxWidth="md"
																						/>
																					)}
																				</>
																			)}
																		</GetFieldValue>
																	</Stack>
																</Box>
																<Box paddingTop="6">
																	<Button
																		onClick={() => fields.remove(index)}
																		title="Remove this ticket type from the activity"
																		level="tertiary"
																		size="sm"
																	>
																		<Icons.Cancel />
																	</Button>
																</Box>
															</Inline>
															{index === (fields.length || 0) - 1 ? (
																<Box paddingTop="6">
																	<Button
																		onClick={() =>
																			fields.push({
																				name: "",
																				durations: [
																					{
																						iso: undefined,
																						slots: undefined,
																					},
																				],
																				pick_up_locations: [undefined],
																				drop_locations: [undefined],
																			})
																		}
																	>
																		Add More Ticket/Package Types
																	</Button>
																</Box>
															) : null}
														</Inline>
														<GetFieldValue<boolean> name="quick_add">
															{({ value: quickAdd }) => (
																<>
																	{quickAdd ? null : (
																		<FieldArray<
																			Required<
																				Required<TNewItemValue>["ticket_types"][number]
																			>["durations"][number]
																		>
																			name={`${name}.durations`}
																		>
																			{({ fields }) => (
																				<Stack gap="4">
																					{fields.map((name, index) => (
																						<Inline gap="4" key={name}>
																							<Inline
																								gap="4"
																								flex="1"
																								flexWrap="wrap"
																							>
																								<TextInputField
																									label="Slots"
																									type="text"
																									name={`${name}.slots`}
																									placeholder="11:00, 13:00"
																									secondaryLabel="optional"
																									help={
																										<GetFieldValue<string>
																											name={`${name}.slots`}
																										>
																											{({ value }) => {
																												if (!value) return null

																												const slots = value
																													.split(",")
																													.map((s) => s.trim())
																												const parsedSlots =
																													slots.map((slot) => {
																														try {
																															const date =
																																parseDate(
																																	slot,
																																	"HH:mm"
																																)

																															return formatDate(
																																date,
																																"HH:mm"
																															)
																														} catch (e) {
																															return "Invalid Time"
																														}
																													})

																												const validSlots =
																													parsedSlots.filter(
																														Boolean
																													)

																												return (
																													<Inline>
																														{validSlots.length >
																															0 && (
																															<Text color="success">
																																{validSlots.join(
																																	", "
																																)}
																															</Text>
																														)}
																													</Inline>
																												)
																											}}
																										</GetFieldValue>
																									}
																								/>
																								<DurationInputField
																									name={`${name}.iso`}
																									label="Duration"
																									secondaryLabel="optional"
																								/>
																							</Inline>
																							<Inline gap="2" paddingTop="6">
																								{Number(fields.length || 0) >
																								1 ? (
																									<Button
																										onClick={() =>
																											fields.remove(index)
																										}
																										size="sm"
																									>
																										<Icons.Cancel />
																									</Button>
																								) : null}
																							</Inline>
																						</Inline>
																					))}
																				</Stack>
																			)}
																		</FieldArray>
																	)}
																</>
															)}
														</GetFieldValue>
													</Inline>
													<GetFieldValue name="use_same_locations">
														{({ value }) =>
															!value && (
																<Inline gap="4" flexWrap="wrap">
																	<Stack gap="2">
																		<Inline gap="1" alignItems="center">
																			<Text as="label" fontWeight="semibold">
																				Pick Up Locations
																			</Text>
																			<Text fontSize="sm" color="muted">
																				(optional)
																			</Text>
																		</Inline>
																		<LocationFields
																			name={`${name}.pick_up_locations`}
																		/>
																		<HotelAsPickupDropToggleField
																			name={`${name}.set_hotel_as_default_pickup`}
																			for="pickup"
																		/>
																	</Stack>
																	<Stack gap="2">
																		<Inline gap="1" alignItems="center">
																			<Text as="label" fontWeight="semibold">
																				Drop Locations
																			</Text>
																			<Text fontSize="sm" color="muted">
																				(optional)
																			</Text>
																		</Inline>
																		<LocationFields
																			name={`${name}.drop_locations`}
																		/>
																		<HotelAsPickupDropToggleField
																			name={`${name}.set_hotel_as_default_drop`}
																			for="drop"
																		/>
																	</Stack>
																</Inline>
															)
														}
													</GetFieldValue>
													<MarkdownInputField
														name={`${name}.description`}
														label="Itinerary/Details"
														secondaryLabel="optional"
														placeholder="Some details regarding this ticket type"
													/>
													<GetFieldValue<
														"" | boolean
													> name="same_closing_days_and_intervals_for_all_tickets">
														{({ value }) =>
															isTruthy(value) ? null : (
																<>
																	<SelectClosedOnDaysOfWeekField
																		name={`${name}.closed_on_days_of_week`}
																	/>
																	<SelectClosedOnDateIntervalsField
																		name={`${name}.closed_on_date_intervals`}
																	/>
																</>
															)
														}
													</GetFieldValue>
												</Stack>
											))}
											{!fields.length ? (
												<Box paddingTop="6">
													<Button
														onClick={() =>
															fields.push({
																name: "",
																durations: [
																	{
																		iso: undefined,
																		slots: undefined,
																	},
																],
																pick_up_locations: [undefined],
																drop_locations: [undefined],
															})
														}
													>
														Add More Ticket/Package Types
													</Button>
												</Box>
											) : null}
										</Stack>
									)}
								</FieldArray>
							</Box>
						</Col>
					</Grid>
					<Divider sm />
					<Box
						padding="4"
						boxShadow="lg"
						rounded="lg"
						borderWidth="1"
						bgColor="subtle"
						marginY="4"
					>
						<Box
							textTransform="uppercase"
							fontSize="sm"
							color="muted"
							marginBottom="4"
							fontWeight="semibold"
							letterSpacing="wider"
						>
							Summary
						</Box>
						<Stack gap="2">
							<FormSpy<TNewItemValue>>
								{({ values }) => {
									const { name, ticket_types } = values

									return ticket_types?.map((t) => (
										<Stack gap="1">
											<Box>
												{!name ? (
													<Heading color="muted">[Title]</Heading>
												) : (
													<Box>
														<Heading>
															{name}
															{t.name ? (
																<Box as="span" marginLeft="1">
																	- {t.name}
																</Box>
															) : null}
														</Heading>
													</Box>
												)}
											</Box>
											<Box color="muted">
												{t.description ? (
													<Markdown>{t.description}</Markdown>
												) : (
													"[Ticket Itinerary]"
												)}
											</Box>
										</Stack>
									))
								}}
							</FormSpy>
						</Stack>
					</Box>
					<Divider sm />
					<Grid gap="4">
						<Col sm={12} md={{ offset: 4, span: 8 }}>
							<Stack gap="4" paddingLeft={{ md: "4" }}>
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Please wait" : "Save Details"}
									</Button>
									{onCancel ? (
										<Button
											onClick={() => onCancel()}
											disabled={submitting}
											level="tertiary"
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}

export function SelectClosedOnDaysOfWeekField({ name }: { name: string }) {
	return (
		<SelectField
			select={Select}
			name={name}
			label="Closed on Days of Week"
			secondaryLabel="optional"
			multiple
			options={[
				"Sunday",
				"Monday",
				"Tuesday",
				"Wednesday",
				"Thursday",
				"Friday",
				"Saturday",
			]}
			help="Please select the day(s) of week where this activity is closed or non-operational"
			placeholder="e.g. Monday, Tuesday"
		/>
	)
}

export function SelectClosedOnDateIntervalsField({ name }: { name: string }) {
	return (
		<Stack gap="1">
			<Text fontWeight="semibold">Closed on Dates / Intervals</Text>
			<FieldArray<Required<TNewItemValue>["closed_on_date_intervals"][number]>
				name={name}
			>
				{({ fields }) => (
					<Stack flexWrap="wrap" gap="2">
						{fields.map((name, index) => (
							<Inline key={index} gap="4">
								<DateRangePickerField
									fromName={`${name}.start_date`}
									toName={`${name}.end_date`}
									rangePresets={null}
								/>
								<Box>
									<Button
										level="tertiary"
										onClick={() => {
											fields.remove(index)
										}}
									>
										<Icons.Cancel />
									</Button>
								</Box>
							</Inline>
						))}
						<Box>
							<Button
								size="sm"
								onClick={() => {
									fields.push({ start_date: undefined, end_date: undefined })
								}}
							>
								<Icons.Plus />{" "}
								{!fields.length ? "Add Date Intervals" : "Add More"}
							</Button>
						</Box>
					</Stack>
				)}
			</FieldArray>
			<Text color="muted" fontSize="sm">
				Select single date or intervals during which the activity/tickets are
				not operational.
			</Text>
		</Stack>
	)
}
